/**
 * AI-transparency label settings (EU AI Act Art. 50), shared by the renderer
 * normalizers, the Remotion compositions and the (vendored) frontend editor.
 * Must stay free of Node imports.
 *
 * Unlike the watermark, the label is ON BY DEFAULT: every clip this system
 * renders is AI-generated, so absence of `ai_label` means "show it" and only an
 * explicit off value (`false` / `"false"` / `0` / `"off"`) hides it. This
 * deliberately breaks the "absent keys normalize byte-identically" convention —
 * a payload from before this feature now normalizes with the label on.
 */

export type AiLabelIcon = 'ai-generated' | 'ai-modified' | 'ai';
/** The four looks the EU package ships: plate color x solid/50%-transparent plate. */
export type AiLabelStyle = 'white-transparent' | 'white' | 'black-transparent' | 'black';
export type AiLabelPosition = 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';

export const DEFAULT_AI_LABEL_ICON: AiLabelIcon = 'ai-generated';
export const DEFAULT_AI_LABEL_STYLE: AiLabelStyle = 'white-transparent';
export const DEFAULT_AI_LABEL_POSITION: AiLabelPosition = 'top-right';

export const AI_LABEL_ICONS_ORDER: readonly AiLabelIcon[] = ['ai-generated', 'ai-modified', 'ai'];
export const AI_LABEL_STYLES_ORDER: readonly AiLabelStyle[] = [
  'white-transparent',
  'white',
  'black-transparent',
  'black',
];
export const AI_LABEL_POSITIONS_ORDER: readonly AiLabelPosition[] = [
  'top-right',
  'top-left',
  'bottom-right',
  'bottom-left',
];

export interface AiLabelSettings {
  /** Visible overlay switch. File-level provenance tags are NOT gated by this. */
  aiLabel: boolean;
  aiLabelIcon: AiLabelIcon;
  aiLabelStyle: AiLabelStyle;
  aiLabelPosition: AiLabelPosition;
  /** Provenance summary the backend aggregates (e.g. "runware/klingai:kling-v2"). */
  aiModels?: string;
}

const OFF_VALUES = new Set(['false', '0', 'off', 'no', 'none', 'hidden']);

/** Only an explicit off value disables the label; anything else (incl. absence) keeps it on. */
export const aiLabelEnabled = (raw: unknown): boolean => {
  if (raw === false || raw === 0) {
    return false;
  }
  if (typeof raw === 'string' && OFF_VALUES.has(raw.trim().toLowerCase())) {
    return false;
  }
  return true;
};

const oneOf = <T extends string>(raw: unknown, allowed: readonly T[], fallback: T): T => {
  const value = typeof raw === 'string' ? (raw.trim().toLowerCase() as T) : undefined;
  return value !== undefined && allowed.includes(value) ? value : fallback;
};

/**
 * Reads `ai_label` / `ai_label_icon` / `ai_label_style` / `ai_label_position`
 * (camelCase tolerated) off clip metadata. Icon/style/position are normalized
 * even when the label is off, so switching it back on restores the user's look.
 */
export const readAiLabel = (metadata: Record<string, unknown>): AiLabelSettings => {
  const aiModels = metadata?.ai_models ?? metadata?.aiModels;
  const models = typeof aiModels === 'string' ? aiModels.trim() : '';
  return {
    aiLabel: aiLabelEnabled(metadata?.ai_label ?? metadata?.aiLabel),
    aiLabelIcon: oneOf(
      metadata?.ai_label_icon ?? metadata?.aiLabelIcon,
      AI_LABEL_ICONS_ORDER,
      DEFAULT_AI_LABEL_ICON,
    ),
    aiLabelStyle: oneOf(
      metadata?.ai_label_style ?? metadata?.aiLabelStyle,
      AI_LABEL_STYLES_ORDER,
      DEFAULT_AI_LABEL_STYLE,
    ),
    aiLabelPosition: oneOf(
      metadata?.ai_label_position ?? metadata?.aiLabelPosition,
      AI_LABEL_POSITIONS_ORDER,
      DEFAULT_AI_LABEL_POSITION,
    ),
    ...(models === '' ? {} : {aiModels: models}),
  };
};
